import { Button, EmptyState, IRestaurantConfiguration, RestaurantUtils } from '@localina/core';
import { DateTime } from 'luxon';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

interface IActiveFilters {
    shift?: string;
    area?: string;
    search?: string;
}

interface ISwitchTarget {
    label: string;
    count: number;
    onClick: () => void;
}

interface IProps {
    configuration: IRestaurantConfiguration;
    dateTime: DateTime;
    noReservations: boolean;
    noFilteredReservations: boolean;
    activeFilters: IActiveFilters;
    switchTarget?: ISwitchTarget;
    onClearFilters: () => void;
    onCreate: () => void;
    onNextDay: () => void;
}

const STROKE_ICON_PROPS = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
} as const;

const FilterGlyph = () => (
    <svg {...STROKE_ICON_PROPS} width={32} height={32} strokeWidth={1.6}>
        <path d="M3.8 5.2h16.4l-6.3 7.4v5.6l-3.8 2v-7.6Z" />
    </svg>
);
const CalendarGlyph = () => (
    <svg {...STROKE_ICON_PROPS} width={32} height={32} strokeWidth={1.6}>
        <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
        <path d="M3.5 9.5h17M8 3.2v3.4M16 3.2v3.4" />
    </svg>
);
const PlusGlyph = () => (
    <svg {...STROKE_ICON_PROPS} width={16} height={16} strokeWidth={2}>
        <path d="M12 5.5v13M5.5 12h13" />
    </svg>
);
const ChevronGlyph = () => (
    <svg {...STROKE_ICON_PROPS} width={16} height={16} strokeWidth={1.7}>
        <path d="m10 6 6 6-6 6" />
    </svg>
);
const ArrowGlyph = () => (
    <svg {...STROKE_ICON_PROPS} width={15} height={15} strokeWidth={1.7}>
        <path d="M5 12h13M12.5 6l6 6-6 6" />
    </svg>
);

// "Monday, 5 October 2026" in the viewer's language, day-first as the Swiss audience reads it.
const formatLongDate = (dateTime: DateTime, language: string) => {
    if (language === 'en') {
        return dateTime.setLocale('en-GB').toFormat('cccc, d LLLL yyyy');
    }
    const formatted = dateTime.setLocale(language).toLocaleString(DateTime.DATE_HUGE);
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

export const ReservationsEmptyState: React.FC<IProps> = (props) => {
    const { t, i18n } = useTranslation();

    if (RestaurantUtils.isClosedAndHasNoShiftsOnDate(props.dateTime, props.configuration)) {
        return (
            <div className="reservations-empty">
                <EmptyState icon={<CalendarGlyph />} title={t('reservations.closeDay')} />
            </div>
        );
    }

    if (props.noReservations) {
        return (
            <div className="reservations-empty">
                <EmptyState
                    icon={<CalendarGlyph />}
                    title={t('reservations.empty.day.title')}
                    text={t('reservations.empty.day.text', { date: formatLongDate(props.dateTime, i18n.language) })}
                    action={
                        <>
                            <Button
                                label={t('reservations.empty.day.create')}
                                startIcon={<PlusGlyph />}
                                onClick={props.onCreate}
                            />
                            <Button
                                secondary
                                label={t('reservations.empty.day.nextDay')}
                                endIcon={<ChevronGlyph />}
                                onClick={props.onNextDay}
                            />
                        </>
                    }
                />
            </div>
        );
    }

    if (!props.noFilteredReservations) {
        return null;
    }

    const { shift, area, search } = props.activeFilters;
    const activeCount = [shift, area, search].filter(Boolean).length;
    const bold = { bold: <b /> };

    let title: string;
    let text: React.ReactNode;
    if (activeCount === 1 && shift) {
        title = t('reservations.empty.filter.shiftTitle', { name: shift });
        text = (
            <>
                <Trans i18nKey="reservations.empty.filter.active" values={{ name: shift }} components={bold} />{' '}
                {t('reservations.empty.filter.shiftElsewhere')}
            </>
        );
    } else if (activeCount === 1 && area) {
        title = t('reservations.empty.filter.areaTitle', { name: area });
        text = (
            <>
                <Trans i18nKey="reservations.empty.filter.active" values={{ name: area }} components={bold} />{' '}
                {t('reservations.empty.filter.areaElsewhere')}
            </>
        );
    } else if (activeCount === 1 && search) {
        title = t('reservations.empty.filter.searchTitle', { query: search });
        text = t('reservations.empty.filter.searchText');
    } else {
        title = t('reservations.empty.filter.manyTitle');
        const names = [shift, area, search && t('reservations.empty.filter.quoted', { query: search })].filter(
            Boolean,
        ) as string[];
        text = names.length ? (
            <>
                {t('reservations.empty.filter.activeMany')}{' '}
                {names.map((name, index) => (
                    <React.Fragment key={name}>
                        {index > 0 && ', '}
                        <b>{name}</b>
                    </React.Fragment>
                ))}
            </>
        ) : undefined;
    }

    return (
        <div className="reservations-empty">
            <EmptyState
                icon={<FilterGlyph />}
                title={title}
                text={text}
                action={<Button label={t('reservations.empty.filter.clear')} onClick={props.onClearFilters} />}
            >
                {props.switchTarget && (
                    <div className="reservations-empty__jump">
                        <button
                            type="button"
                            className="reservations-empty__jump-button"
                            onClick={props.switchTarget.onClick}
                        >
                            {t('reservations.empty.filter.switchTo', { name: props.switchTarget.label })}
                            <b>{props.switchTarget.count}</b>
                            <ArrowGlyph />
                        </button>
                    </div>
                )}
            </EmptyState>
        </div>
    );
};
